import {
  Download01Icon,
  GitBranch01Icon,
  Settings01Icon,
} from '@langchain/untitled-ui-icons';

import { Dispatch, SetStateAction, useState } from 'react';

import { useAlignEvaluatorInfo } from '@/Pages/Playground/PlaygroundHome/hooks/useAlignEvaluator';
import { DATA_CONVERTERS } from '@/components/Code/Code.utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import useToast from '@/components/Toast/useToast';
import { Button, IconButton, Tooltip } from '@/design-system';
import { GroupedTabs } from '@/design-system/components/GroupedTabs/GroupedTabs';
import { useDataset, useOrganizationId } from '@/hooks/useSwr';
import { ExperimentViewOverridesSchema, SessionSchema } from '@/types/schema';
import { downloadDatasetSessionsCSV } from '@/utils/datasets';
import { cn } from '@/utils/tailwind';

import { DatasetSessionCompareSettings, TextDisplayMode } from '../constants';
import { ExperimentLanguage } from '../types';
import { isInPlayground } from '../utils/isInPlayground';
import { BaselineSessionSelectPopover } from './BaselineSessionSelectPopover';
import { ColumnVisibilityPopover } from './ColumnVisibilityPopover';
import { CompareExperimentsTabs } from './CompareExperimentTabs/CompareExperimentTabs';
import { DatasetSessionCompareAddExperimentPopover } from './DatasetSessionCompareAddExperimentPopover';
import { ExperimentViewConfigPane } from './ExperimentViewConfigPane/index';
import { GroupByHeaderButton } from './ExperimentsGroupByTable/GroupByHeaderButton';
import { useGroupByMetadata } from './ExperimentsGroupByTable/useGroupByMetadata';
import { TracingProjectPreview } from './TracingProjectPreview';

const TextDisplayToggle = ({
  textDisplayMode,
  setTextDisplayMode,
  diffDisabled,
  language,
  setLanguage,
}: {
  textDisplayMode: TextDisplayMode;
  setTextDisplayMode: (textDisplayMode: TextDisplayMode) => void;
  diffDisabled?: string;
  language: ExperimentLanguage;
  setLanguage: (language: ExperimentLanguage) => void;
}) => {
  const handleTextDisplayToggleChange = (value: TextDisplayMode) => {
    setTextDisplayMode(value);
    if (value === TextDisplayMode.DIFF && language === 'pretty') {
      setLanguage('json');
    }
  };

  return (
    <GroupedTabs<TextDisplayMode>
      value={textDisplayMode}
      onChange={handleTextDisplayToggleChange}
      options={[
        {
          display: 'Compact',
          value: TextDisplayMode.COMPACT,
        },
        { display: 'Full', value: TextDisplayMode.FULL },
        {
          display: (
            <Tooltip title={diffDisabled}>
              <span>Diff</span>
            </Tooltip>
          ),
          value: TextDisplayMode.DIFF,
          disabled: !!diffDisabled,
        },
      ]}
      size="md"
    />
  );
};

const DataLanguageDisplayToggle = ({
  language,
  setLanguage,
  textDisplayMode,
}: {
  language: ExperimentLanguage;
  setLanguage: (language: ExperimentLanguage) => void;
  textDisplayMode: TextDisplayMode;
}) => {
  const inPlayground = isInPlayground();
  const disabled = textDisplayMode === TextDisplayMode.COMPACT;
  return (
    <Select
      value={language}
      onValueChange={(value) => setLanguage(value as ExperimentLanguage)}
      disabled={disabled}
    >
      <Tooltip title={disabled ? 'Switch to full mode to change language' : ''}>
        <SelectTrigger
          className={cn(
            'h-[34px] bg-background py-[4px] font-medium',
            inPlayground && 'border-primary'
          )}
        >
          <SelectValue>
            {language === 'pretty' ? 'Default' : language.toUpperCase()}
          </SelectValue>
        </SelectTrigger>
      </Tooltip>

      <SelectContent>
        <Tooltip
          title={
            textDisplayMode === TextDisplayMode.DIFF
              ? 'Diff is only supported for JSON and YAML'
              : ''
          }
          side="right"
        >
          <div>
            <SelectItem
              key={'pretty'}
              value={'pretty'}
              className="cursor-pointer bg-background font-medium hover:bg-secondary"
              checkmarkPosition="right"
              disabled={textDisplayMode === TextDisplayMode.DIFF}
            >
              Default
            </SelectItem>
          </div>
        </Tooltip>
        {Object.keys(DATA_CONVERTERS).map((lang) => (
          <SelectItem
            key={lang}
            value={lang}
            className="cursor-pointer bg-background font-medium uppercase hover:bg-secondary"
            checkmarkPosition="right"
          >
            {lang}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const DatasetSessionControlHeader = ({
  sessions,
  displaySettingsState,
  datasetShareToken,
  datasetId,
  datasetName,
  setAddedSessions,
  aggregateFeedbackKeys,
  pageTitle,
  noAttachments,
  baselineSessionId,
  setBaselineSessionId,
  selectedFeedbackKey,
  setSelectedFeedbackKey,
  onFeedbackConfigUpdate,
  onExperimentViewUpdate,
  experimentView,
  mutateDelta,
  allowDownload,
  isPairwise,
  isEditable,
  numSessions,
}: {
  sessions: Pick<SessionSchema, 'id' | 'name' | 'run_facets'>[];
  displaySettingsState: DatasetSessionCompareSettings;
  datasetShareToken?: string;
  datasetId: string;
  datasetName: string;
  setAddedSessions?: Dispatch<SetStateAction<string[]>>;
  aggregateFeedbackKeys: string[];
  pageTitle?: string;
  noAttachments: boolean;
  baselineSessionId?: string;
  setBaselineSessionId?: (sessionId: string) => void;
  selectedFeedbackKey?: string;
  setSelectedFeedbackKey?: (feedbackKey: string) => void;
  mutateDelta?: () => void;
  onFeedbackConfigUpdate?: () => void;
  onExperimentViewUpdate?: (
    experimentView?: ExperimentViewOverridesSchema
  ) => void;
  experimentView?: ExperimentViewOverridesSchema;
  allowDownload?: boolean;
  isPairwise?: boolean;
  isEditable?: boolean;
  numSessions?: number;
}) => {
  const inPlayground = isInPlayground();
  const [isTracingProjectOpen, setIsTracingProjectOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExperimentViewConfigOpen, setIsExperimentViewConfigOpen] =
    useState(false);
  const { metadataKey: groupByMetadataKey } = useGroupByMetadata();
  const organizationId = useOrganizationId() ?? '';
  const toast = useToast();
  const { data: dataset } = useDataset(datasetId);
  const { isAlignEvaluatorV2 } = useAlignEvaluatorInfo();
  const handleDownloadCSV = async () => {
    try {
      setIsDownloading(true);
      const code = await downloadDatasetSessionsCSV(
        organizationId,
        datasetId,
        sessions.map((s) => s.id)
      );
      if (code === 206) {
        toast.createToast({
          title: 'Download limit hit',
          description: `Only the first 1000 rows were downloaded due to size limitations.`,
        });
      }
    } catch (error) {
      console.error('Failed to download CSV:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewProjectButtonClick = () => setIsTracingProjectOpen(true);
  const handleConfigureColumnsButtonClick = () => {
    setIsExperimentViewConfigOpen(true);
  };

  if (isAlignEvaluatorV2) {
    return null;
  }

  return (
    <div className="flex min-w-0 shrink-0 items-center gap-1.5">
      {numSessions && numSessions > 1 && <CompareExperimentsTabs />}

      {allowDownload ? (
        <IconButton
          label="Download as CSV"
          onClick={handleDownloadCSV}
          disabled={isDownloading || sessions.length === 0}
          loading={isDownloading}
          color="secondary"
          icon={Download01Icon}
          size="md"
        />
      ) : null}

      {sessions.length > 1 &&
        setBaselineSessionId &&
        setSelectedFeedbackKey &&
        !isPairwise && (
          <BaselineSessionSelectPopover
            sessions={sessions}
            baselineSessionId={baselineSessionId}
            setBaselineSessionId={setBaselineSessionId}
            aggregateFeedbackKeys={aggregateFeedbackKeys}
            selectedFeedbackKey={selectedFeedbackKey}
            setSelectedFeedbackKey={setSelectedFeedbackKey}
            mutateDelta={mutateDelta}
            onFeedbackConfigUpdate={onFeedbackConfigUpdate}
          />
        )}

      {!inPlayground && (
        <TextDisplayToggle
          language={displaySettingsState.language}
          textDisplayMode={displaySettingsState.textDisplayMode}
          setTextDisplayMode={displaySettingsState.setTextDisplayMode}
          diffDisabled={
            sessions.length > 1
              ? 'Diff is only supported for single experiment'
              : sessions.length === 0
              ? 'Run experiment to see diff'
              : experimentView?.column_overrides?.some(
                  (col) => !col.hide && col.column.startsWith('outputs.')
                )
              ? 'Diff is only supported if there are no computed output columns'
              : undefined
          }
          setLanguage={displaySettingsState.setLanguage}
        />
      )}

      {!isEditable && (
        <DataLanguageDisplayToggle
          language={displaySettingsState.language}
          setLanguage={displaySettingsState.setLanguage}
          textDisplayMode={displaySettingsState.textDisplayMode}
        />
      )}

      {sessions.length === 1 && !inPlayground && (
        <GroupByHeaderButton facets={sessions[0].run_facets ?? []} />
      )}

      {sessions.length > 1 && (
        <ColumnVisibilityPopover
          displaySettingsState={displaySettingsState}
          aggregateFeedbackKeys={aggregateFeedbackKeys}
          noAttachments={noAttachments}
          sessions={sessions}
        />
      )}

      {sessions.length === 1 && !datasetShareToken && !inPlayground && (
        <>
          <Tooltip title="Configure experiment view">
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleConfigureColumnsButtonClick}
              leftDecorator={Settings01Icon}
              size="md"
            >
              Display
            </Button>
          </Tooltip>
          <IconButton
            color="secondary"
            label="View project"
            onClick={handleViewProjectButtonClick}
            icon={GitBranch01Icon}
            size="md"
          />
        </>
      )}

      {setAddedSessions && (
        <DatasetSessionCompareAddExperimentPopover
          sessionIds={sessions.map((session) => session.id)}
          setAddedSessions={setAddedSessions}
          datasetName={datasetName}
          datasetShareToken={datasetShareToken}
          setTextDisplayMode={displaySettingsState.setTextDisplayMode}
          textDisplayMode={displaySettingsState.textDisplayMode}
          disabled={!!groupByMetadataKey}
        />
      )}

      {datasetShareToken || sessions.length !== 1 ? null : (
        <TracingProjectPreview
          isOpen={isTracingProjectOpen}
          onClose={() => setIsTracingProjectOpen(false)}
          sessionId={sessions[0].id}
          tracingProjectTitle={pageTitle ?? 'Tracing project'}
        />
      )}

      {dataset && (
        <ExperimentViewConfigPane
          dataset={dataset}
          aggregateFeedbackKeys={aggregateFeedbackKeys}
          noAttachments={noAttachments}
          open={isExperimentViewConfigOpen}
          facets={sessions?.[0]?.run_facets}
          onClose={() => {
            setIsExperimentViewConfigOpen(false);
          }}
          onSuccess={(experimentView) => {
            onExperimentViewUpdate?.(experimentView);
            const hasComputedOutputColumns =
              experimentView?.column_overrides?.some(
                (col) => !col.hide && col.column.startsWith('outputs.')
              );
            if (
              hasComputedOutputColumns &&
              displaySettingsState.textDisplayMode === TextDisplayMode.DIFF
            ) {
              displaySettingsState.setTextDisplayMode(TextDisplayMode.FULL);
            }
          }}
        />
      )}
    </div>
  );
};
